import { getRuntimeFlagGroupBySlug } from "../lib/runtime-flag-groups";

export interface AdminBreadcrumb {
  label: string;
  href?: string;
}

export interface AdminRouteMeta {
  title: string;
  breadcrumbs: AdminBreadcrumb[];
}

const compactId = (value: string): string => `${value.slice(0, 8)}...`;

const titleBySegment: Record<string, string> = {
  brands: "Brands",
  stats: "Product Stats",
  users: "Users",
  workspaces: "Workspaces",
  payments: "Payments",
  "billing-plans": "Billing Plans",
  "credit-packs": "Run Packs",
  runs: "Worker Runs",
  "image-regenerations": "Image Regenerations",
  calendar: "Calendar Operations",
  "instagram-debug": "Instagram Publishing",
  "release-access": "Release Access",
  "runtime-flags": "Runtime Flags",
  "model-pricing": "Model Pricing",
  "social-post-planner": "Social Planner",
  "carousel-formats": "Carousel Formats",
  "layout-templates": "Layout Templates",
  campaigns: "Campaigns",
  "planned-posts": "Planned Posts",
};

export const getAdminRouteMeta = (pathname: string): AdminRouteMeta => {
  const segments = pathname.split("/").filter(Boolean);

  if (segments.length === 0) {
    return { title: "Brands", breadcrumbs: [{ label: "Brands", href: "/brands" }] };
  }

  const breadcrumbs: AdminBreadcrumb[] = [];
  const push = (label: string, href?: string) => breadcrumbs.push({ label, href });

  const firstSegment = segments[0]!;

  if (firstSegment === "brands") {
    push("Brands", "/brands");
    const brandId = segments[1];
    if (brandId) {
      push(`Brand ${compactId(brandId)}`, `/brands/${brandId}`);
    }

    if (segments[2] === "campaigns") {
      push("Campaigns", brandId ? `/brands/${brandId}/campaigns` : undefined);
      const campaignId = segments[3];
      if (campaignId) {
        push(`Campaign ${compactId(campaignId)}`, `/brands/${brandId}/campaigns/${campaignId}`);
      }

      const postId = segments[5];
      if (segments[4] === "planned-posts" && postId) {
        push(`Post ${compactId(postId)}`);
        return { title: "Planned Post Workspace", breadcrumbs };
      }

      return { title: campaignId ? "Campaign Board" : "Campaigns", breadcrumbs };
    }

    if (segments[2] === "social-post-planner") {
      push("Social Planner");
      return { title: "Social Planner", breadcrumbs };
    }

    return { title: brandId ? "Brand Workspace" : "Brands", breadcrumbs };
  }

  if (firstSegment === "users") {
    push("Users", "/users");
    const userId = segments[1];
    if (userId) {
      push(`User ${compactId(userId)}`);
      return { title: "User Workspace", breadcrumbs };
    }

    return { title: "Users", breadcrumbs };
  }

  if (firstSegment === "workspaces") {
    push("Workspaces", "/workspaces");
    const workspaceId = segments[1];
    if (workspaceId) {
      push(`Workspace ${compactId(workspaceId)}`);
      return { title: "Workspace Billing", breadcrumbs };
    }

    return { title: "Workspaces", breadcrumbs };
  }

  if (firstSegment === "runtime-flags") {
    push("Runtime Flags", "/runtime-flags");

    const groupSlug = segments[1];
    if (groupSlug) {
      const group = getRuntimeFlagGroupBySlug(groupSlug);
      push(group?.title ?? "Unknown Category");
      return { title: group?.title ?? "Runtime Flags", breadcrumbs };
    }

    return { title: "Runtime Flags", breadcrumbs };
  }

  const label = titleBySegment[firstSegment] ?? firstSegment;
  push(label, `/${firstSegment}`);
  return { title: label, breadcrumbs };
};
